import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { Check, Loader2, Lock, LogOut, Mail, Phone, Receipt, User, Users } from "lucide-react";

import { formatPhone, isValidPhone } from "@montebox/shared/people";

import { AdminCard } from "@/components/admin/primitives";
import { PasswordChecklist, isStrongPassword } from "@/components/PasswordChecklist";
import { formatDate } from "@/lib/dates";
import { countLabel } from "@/lib/format";
import {
  useCancelEmailChange,
  useChangePassword,
  useMyOrders,
  useUpdateProfile,
} from "@/lib/queries";
import { useSession } from "@/lib/session";

/**
 * Meus dados (cliente).
 *
 * Nome, WhatsApp e e-mail sao os campos que viajam em cada pedido — editar
 * aqui e preencher o proximo pedido de antemao. O e-mail tambem e o login,
 * entao a tela avisa.
 */
export const Route = createFileRoute("/minha-conta/_conta/dados")({
  head: () => ({ meta: [{ title: "Meus dados — MonteBox" }] }),
  component: ClienteDados,
});

function ClienteDados() {
  const { profile, refreshProfile, logout } = useSession();
  const orders = useMyOrders();
  const updateProfile = useUpdateProfile();
  const cancelEmailChange = useCancelEmailChange();

  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [team, setTeam] = useState("");

  // O formulario comeca com o que ja esta salvo e reseta se o perfil mudar.
  useEffect(() => {
    if (!profile) return;
    setName(profile.name);
    setWhatsapp(formatPhone(profile.whatsapp ?? ""));
    setEmail(profile.email);
    setTeam(profile.teamName ?? "");
  }, [profile]);

  const phoneInvalid = whatsapp !== "" && !isValidPhone(whatsapp);

  const dirty =
    profile !== null &&
    (name !== profile.name ||
      whatsapp !== formatPhone(profile.whatsapp ?? "") ||
      email !== profile.email ||
      team !== (profile.teamName ?? ""));

  const reset = () => {
    if (!profile) return;
    setName(profile.name);
    setWhatsapp(formatPhone(profile.whatsapp ?? ""));
    setEmail(profile.email);
    setTeam(profile.teamName ?? "");
  };

  const emailChanged =
    profile !== null && email.trim().toLowerCase() !== profile.email.toLowerCase();

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const current = profile?.email ?? "";
    updateProfile.mutate(
      // O e-mail só vai quando mudou: ele não troca na hora, vira um pedido
      // confirmado pelo e-mail atual.
      { name, whatsapp, teamName: team, ...(emailChanged ? { email: email.trim() } : {}) },
      {
        onSuccess: async () => {
          await refreshProfile();
          if (emailChanged) {
            toast.success(
              `Enviamos um link para ${current} para confirmar a troca. Até lá, o seu e-mail continua o mesmo.`,
              { duration: 8000 },
            );
          } else {
            toast.success("Dados atualizados.");
          }
        },
        onError: (cause) =>
          toast.error(cause instanceof Error ? cause.message : "Não foi possível salvar."),
      },
    );
  };

  return (
    <div className="mx-auto flex max-w-7xl flex-col px-4 py-6 sm:px-6 sm:py-10">
      <h1 className="font-display text-3xl font-bold tracking-tight text-mb-navy">Meus dados</h1>
      <p className="mt-2 max-w-2xl text-sm text-mb-muted text-pretty">
        São os dados que a MonteBox usa para responder seu orçamento. Eles já vêm preenchidos nos
        próximos pedidos.
      </p>

      <div className="mt-6 grid items-start gap-6 sm:mt-8 md:grid-cols-[260px_1fr]">
        {/* Navegação lateral (no celular, uma faixa rolável em cima) */}
        <nav className="-mx-4 flex gap-1 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:flex-col md:overflow-visible md:px-0">
          <Link
            to="/minha-conta/pedidos"
            className="flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-mb-field px-4 py-3 text-sm font-semibold text-mb-muted transition-colors hover:bg-white hover:text-mb-navy"
          >
            <Receipt className="h-[18px] w-[18px]" />
            Meus pedidos
            <span className="ml-auto grid h-5 min-w-5 place-items-center rounded-full bg-mb-line px-1.5 text-[11px] font-bold text-mb-navy">
              {orders.data?.length ?? 0}
            </span>
          </Link>
          <Link
            to="/minha-conta/dados"
            className="flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-mb-field px-4 py-3 text-sm font-semibold text-mb-muted transition-colors hover:bg-white hover:text-mb-navy data-[status=active]:bg-white data-[status=active]:font-bold data-[status=active]:text-mb-navy data-[status=active]:shadow-card"
          >
            <User className="h-[18px] w-[18px]" />
            Meus dados
          </Link>
          <a
            href="#senha"
            className="flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-mb-field px-4 py-3 text-sm font-semibold text-mb-muted transition-colors hover:bg-white hover:text-mb-navy"
          >
            <Lock className="h-[18px] w-[18px]" />
            Senha e acesso
          </a>
          <button
            type="button"
            onClick={() => void logout()}
            className="flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-mb-field px-4 py-3 text-sm font-semibold text-mb-muted transition-colors hover:bg-white hover:text-destructive md:mt-2"
          >
            <LogOut className="h-[18px] w-[18px]" />
            Sair da conta
          </button>
        </nav>

        {/* Formulário */}
        <div className="flex flex-col gap-6">
          <AdminCard className="p-5 sm:p-6">
            <div className="flex items-center gap-4">
              <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-mb-surface font-display text-lg font-bold text-mb-navy">
                {initials(profile?.name ?? "")}
              </span>
              <div className="min-w-0">
                <p className="truncate font-display text-lg font-bold text-mb-navy">
                  {profile?.name}
                </p>
                <p className="text-xs text-mb-muted">
                  {profile ? `Cliente desde ${formatDate(profile.createdAt)}` : ""} ·{" "}
                  {countLabel(orders.data?.length ?? 0, "pedido", "pedidos")}
                </p>
              </div>
            </div>
          </AdminCard>

          <AdminCard className="p-5 sm:p-6">
            <h2 className="font-display text-base font-bold text-mb-navy">Contato</h2>

            <form className="mt-5 flex flex-col gap-5" onSubmit={handleSubmit}>
              <Field
                label="Nome"
                icon={<User className="h-[18px] w-[18px] text-mb-muted" />}
                value={name}
                onChange={setName}
                autoComplete="name"
              />
              <Field
                label="WhatsApp"
                icon={<Phone className="h-[18px] w-[18px] text-mb-muted" />}
                value={whatsapp}
                onChange={(value) => setWhatsapp(formatPhone(value))}
                autoComplete="tel"
                hint={
                  phoneInvalid
                    ? "Com DDD: (XX) XXXX-XXXX ou (XX) X XXXX-XXXX."
                    : "É por aqui que a equipe fala com você sobre o pedido."
                }
              />
              <Field
                label="E-mail"
                icon={<Mail className="h-[18px] w-[18px] text-mb-muted" />}
                value={email}
                onChange={setEmail}
                type="email"
                autoComplete="email"
                hint={
                  emailChanged
                    ? "Ao salvar, o e-mail atual recebe um link para confirmar a troca."
                    : "Também é o seu login."
                }
              />
              {profile?.pendingEmail && (
                <div className="-mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-mb-field border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
                  <span className="grow">
                    Troca para <strong>{profile.pendingEmail}</strong> esperando confirmação — abra
                    o link que enviamos para <strong>{profile.email}</strong>.
                  </span>
                  <button
                    type="button"
                    disabled={cancelEmailChange.isPending}
                    onClick={() =>
                      cancelEmailChange.mutate(undefined, {
                        onSuccess: async () => {
                          await refreshProfile();
                          toast.success("Troca de e-mail cancelada.");
                        },
                      })
                    }
                    className="shrink-0 font-bold underline underline-offset-2 hover:text-amber-950 disabled:opacity-50"
                  >
                    Cancelar troca
                  </button>
                </div>
              )}
              <Field
                label="Time ou empresa"
                optional
                icon={<Users className="h-[18px] w-[18px] text-mb-muted" />}
                value={team}
                onChange={setTeam}
                placeholder="Ex.: Grêmio Recreativo Vila Nova"
              />

              <div className="flex items-center justify-end gap-3 border-t border-mb-line pt-5">
                <button
                  type="button"
                  onClick={reset}
                  disabled={!dirty}
                  className="rounded-full px-4 py-2.5 text-sm font-semibold text-mb-muted transition-colors hover:text-mb-navy disabled:opacity-40"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={!dirty || updateProfile.isPending}
                  className="flex items-center gap-2 rounded-mb-button bg-mb-yellow px-5 py-3 font-display text-sm font-bold text-mb-ink shadow-cta transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
                >
                  {updateProfile.isPending ? (
                    <Loader2 className="h-[18px] w-[18px] animate-spin" />
                  ) : (
                    <Check className="h-[18px] w-[18px] stroke-[3]" />
                  )}
                  Salvar alterações
                </button>
              </div>
            </form>
          </AdminCard>

          <div id="senha" className="scroll-mt-24">
            <PasswordCard passwordUpdatedAt={profile?.passwordUpdatedAt ?? null} />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Troca de senha — pede a senha atual e derruba as outras sessoes. */
function PasswordCard({ passwordUpdatedAt }: { passwordUpdatedAt: string | null }) {
  const changePassword = useChangePassword();
  const { logout, profile } = useSession();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    changePassword.mutate(
      { currentPassword: current, newPassword: next },
      {
        onSuccess: async () => {
          // O backend revoga TODAS as sessoes, inclusive esta: entrar de novo
          // com a senha nova e o unico caminho coerente.
          toast.success("Senha alterada. Entre novamente com a senha nova.");
          await logout();
        },
        onError: (cause) =>
          toast.error(cause instanceof Error ? cause.message : "Não foi possível alterar."),
      },
    );
  };

  return (
    <AdminCard className="p-5 sm:p-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-base font-bold text-mb-navy">Senha</h2>
          <p className="mt-1 text-sm text-mb-muted">
            {passwordUpdatedAt
              ? `Alterada pela última vez em ${formatDate(passwordUpdatedAt)}.`
              : "Nunca alterada."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="flex shrink-0 items-center gap-1.5 rounded-full border border-mb-line px-4 py-2 text-sm font-semibold text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface"
        >
          <Lock className="h-4 w-4" />
          {open ? "Cancelar" : "Alterar senha"}
        </button>
      </div>

      {open && (
        <form
          className="mt-5 grid items-end gap-3 sm:grid-cols-[1fr_1fr_auto]"
          onSubmit={handleSubmit}
        >
          <label className="block">
            <span className="text-xs font-bold text-mb-ink">Senha atual</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
              className="mt-1.5 h-11 w-full rounded-mb-field border border-mb-line px-4 text-sm outline-none focus:border-mb-navy"
            />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-mb-ink">Nova senha</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              value={next}
              onChange={(event) => setNext(event.target.value)}
              className="mt-1.5 h-11 w-full rounded-mb-field border border-mb-line px-4 text-sm outline-none focus:border-mb-navy"
            />
          </label>
          <button
            type="submit"
            disabled={
              changePassword.isPending ||
              !current ||
              !isStrongPassword(next, { name: profile?.name, email: profile?.email })
            }
            className="flex h-11 items-center gap-2 rounded-mb-button bg-mb-navy px-5 text-sm font-bold text-white disabled:opacity-60"
          >
            {changePassword.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Salvar
          </button>
          <PasswordChecklist
            password={next}
            name={profile?.name}
            email={profile?.email}
            className="text-mb-muted sm:col-span-3"
          />
          <p className="text-xs text-mb-muted sm:col-span-3">
            Ao trocar a senha, todas as sessões abertas são encerradas — inclusive esta.
          </p>
        </form>
      )}
    </AdminCard>
  );
}

/** Campo de texto do formulario — rotulo, icone e dica opcional. */
function Field({
  label,
  icon,
  value,
  onChange,
  type = "text",
  autoComplete,
  placeholder,
  hint,
  optional,
}: {
  label: string;
  icon: ReactNode;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  autoComplete?: string;
  placeholder?: string;
  hint?: string;
  optional?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-sm font-bold text-mb-ink">
        {label}
        {optional && <span className="font-semibold text-mb-muted"> (opcional)</span>}
      </span>
      <span className="mt-2 flex h-12 items-center gap-2.5 rounded-mb-field border border-mb-line bg-white px-4 focus-within:border-mb-navy focus-within:ring-[3px] focus-within:ring-mb-yellow/35">
        <span className="shrink-0">{icon}</span>
        <input
          type={type}
          value={value}
          autoComplete={autoComplete}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          className="w-full bg-transparent text-sm text-mb-ink outline-none placeholder:text-mb-muted"
        />
      </span>
      {hint && <p className="mt-1.5 text-xs text-mb-muted">{hint}</p>}
    </label>
  );
}

const initials = (name: string): string =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
